import axios from 'axios';
import useAuthStore from '../../store/authStore';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clearUser();
    }
    return Promise.reject(error);
  }
);

export const getBlogs = async () => {
  try {
    const response = await api.get('/admin/blogs');
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch blogs' };
  }
};

export const getBlogById = async (id) => {
  try {
    const response = await api.get(`/admin/blog/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to fetch blog' };
  }
};

export const createBlog = async (blogData) => {
  try {
    // Create FormData for file uploads
    const formData = new FormData();
    
    // Handle featured image
    if (blogData.featuredImage instanceof File) {
      formData.append('featuredImage', blogData.featuredImage);
      delete blogData.featuredImage;
    }
    
    // Handle cover image
    if (blogData.coverImage instanceof File) {
      formData.append('coverImage', blogData.coverImage);
      delete blogData.coverImage;
    }
    
    // Add all other blog data
    Object.keys(blogData).forEach(key => {
      if (blogData[key] !== undefined) {
        // Handle arrays (like tags)
        if (Array.isArray(blogData[key])) {
          formData.append(key, JSON.stringify(blogData[key]));
        } else {
          formData.append(key, blogData[key]);
        }
      }
    });
    
    const response = await api.post('/admin/add-blog', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to create blog' };
  }
};

// Update an existing blog
export const updateBlog = async ({ id, ...blogData }) => {
  try {
    const formData = new FormData();
    
    // Handle featured image
    if (blogData.featured_image instanceof File) {
      formData.append('featuredImage', blogData.featured_image);
      delete blogData.featured_image;
    }
    
    // Handle cover image
    if (blogData.cover_image instanceof File) {
      formData.append('coverImage', blogData.cover_image);
      delete blogData.cover_image;
    }
    
    // Add all other blog data
    Object.keys(blogData).forEach(key => {
      if (blogData[key] !== undefined && blogData[key] !== null) {
        if (Array.isArray(blogData[key])) {
          formData.append(key, JSON.stringify(blogData[key]));
        } else {
          formData.append(key, blogData[key]);
        }
      }
    });
    
    const response = await api.put(`/admin/update-blog/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to update blog' };
  }
};

export const deleteBlog = async (id) => {
  try {
    const response = await api.delete(`/admin/blog/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to delete blog' };
  }
};

// Helper function to validate blog data before sending to API
export const validateBlogData = (blogData) => {
  const errors = {};

  if (!blogData.title?.trim()) {
    errors.title = 'Title is required';
  }

  if (!blogData.content?.trim()) {
    errors.content = 'Content is required';
  }

  if (!blogData.category?.trim()) {
    errors.category = 'Category is required';
  }

  if (blogData.tags && !Array.isArray(blogData.tags)) {
    errors.tags = 'Tags must be an array';
  }

  if (blogData.featuredImage && !(blogData.featuredImage instanceof File)) {
    errors.featuredImage = 'Featured image must be a file';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};